/**
 * Ace-68-Turbo / GCOME_AP 改键分流。
 *
 * 常规、多媒体、宏仍走按键矩阵，只是 Type 与 keycode.json / CH585M 不同；
 * 高级改键（DKS/MT/TGL/RS/SOCD）存在独立列表，矩阵只存 slot 指针。
 *
 * 本文件只做纯解析/构造，不直接读写设备。
 * 标准改键复用 keyMapSettings.ts，不在那边继续加 GCOME case。
 */
import type {
  DeviceManifest,
  DksContactState,
  DksKeyInfo,
  DksSlotInfo,
  KeyDisplayOverride,
  KeyFeaturesCapability,
  keyMapItem,
  KeyTravelList,
  MtSlotInfo,
  TglSlotInfo,
} from '@rapoo/device-sdk'
import { KeyTravelFamily } from '@rapoo/device-sdk'
import type { KeyConfig } from '@rapoo/element'
import type { KeySetting, KeyType, LayoutKey } from '../types'
import {
  buildRestoreKeyMapPayload,
  buildSdkKeyMapPayloadFromSetting,
  getKeyConfigByTypeCode,
  getLocalizedField,
  KEYCODE_KEYWORDS_MAP,
  parseLayerMapToKeySettings,
} from './keyMapSettings'
import { findSettingByTargetKey, parseNumber } from './keyIdentity'

export type GcomeTriple = {
  type: number
  codeL: number
  codeH: number
}

/** GCOME_AP 矩阵 Type（与 SDK disposeRead 一致）。 */
export const GCOME_MATRIX_TYPE = {
  DISABLE: 0x00,
  KEYBOARD: 0x10,
  MOUSE: 0x20,
  CONSUMER: 0x30,
  SYSTEM: 0x40,
  MACRO: 0x70,
  DKS: 0x90,
  TGL: 0x91,
  MT: 0x92,
  RS: 0x93,
  SOCD: 0x94,
  FN: 0xf0,
} as const

const ADVANCED_MATRIX_TYPES = new Set<number>([
  GCOME_MATRIX_TYPE.DKS,
  GCOME_MATRIX_TYPE.TGL,
  GCOME_MATRIX_TYPE.MT,
  GCOME_MATRIX_TYPE.RS,
  GCOME_MATRIX_TYPE.SOCD,
])

const ADVANCED_KEY_TYPES = new Set<KeyType>(['DKS', 'MT', 'TGL', 'RS', 'SOCD'])

/** HID 修饰键 usage（0xE0-0xE7）↔ GCOME Code0 修饰位图。 */
const HID_MODIFIER_TO_BIT: Record<number, number> = {
  0xe0: 0x01,
  0xe1: 0x02,
  0xe2: 0x04,
  0xe3: 0x08,
  0xe4: 0x10,
  0xe5: 0x20,
  0xe6: 0x40,
  0xe7: 0x80,
}

const BIT_TO_HID_MODIFIER: Record<number, number> = Object.fromEntries(
  Object.entries(HID_MODIFIER_TO_BIT).map(([hid, bit]) => [bit, Number(hid)]),
)

/** keycode.json / CH585M type → GCOME 矩阵 Type。 */
const KEYCODE_TYPE_TO_GCOME: Record<number, number> = {
  0x00: GCOME_MATRIX_TYPE.DISABLE,
  0x01: GCOME_MATRIX_TYPE.KEYBOARD,
  0x02: GCOME_MATRIX_TYPE.CONSUMER,
  0x03: GCOME_MATRIX_TYPE.SYSTEM,
  0x04: GCOME_MATRIX_TYPE.MOUSE,
  0x08: GCOME_MATRIX_TYPE.MACRO,
  0x09: GCOME_MATRIX_TYPE.FN,
}

export type AdvancedSlotKind = 'dks' | 'mt' | 'tgl'

export type AdvancedLists = {
  dksList: DksSlotInfo[]
  mtList: MtSlotInfo[]
  tglList: TglSlotInfo[]
  travelList?: KeyTravelList
}

export type AdvancedListWrite =
  | { kind: 'dks'; index: number; slot: DksSlotInfo }
  | { kind: 'mt'; index: number; slot: MtSlotInfo }
  | { kind: 'tgl'; index: number; slot: TglSlotInfo }

export type AdvancedListClear = { kind: AdvancedSlotKind; index: number }

/**
 * 一次高级改键下发计划。
 * 执行顺序：先写新列表 → 再写矩阵绑定 → 最后清旧 slot。
 */
export type AdvancedWritePlan = {
  listWrites: AdvancedListWrite[]
  matrixItems: keyMapItem[]
  listClears: AdvancedListClear[]
}

const EMPTY_CONTACT_STATE: DksContactState = {
  segments: [
    { selected: false, continuous: false, nextPointState: false },
    { selected: false, continuous: false, nextPointState: false },
    { selected: false, continuous: false, nextPointState: false },
  ],
  final: false,
}

const createEmptyDksKey = (): DksKeyInfo => ({
  type: 0,
  codeL: 0,
  codeH: 0,
  state: EMPTY_CONTACT_STATE,
})

export const createEmptyDksSlot = (index: number): DksSlotInfo => ({
  index,
  travels: [0, 0, 0, 0],
  keys: [
    createEmptyDksKey(),
    createEmptyDksKey(),
    createEmptyDksKey(),
    createEmptyDksKey(),
  ] as DksSlotInfo['keys'],
})

export const createEmptyMtSlot = (index: number): MtSlotInfo => ({
  index,
  tap: { type: 0, codeL: 0, codeH: 0 },
  hold: { type: 0, codeL: 0, codeH: 0 },
})

export const createEmptyTglSlot = (index: number): TglSlotInfo => ({
  index,
  type: 0,
  codeL: 0,
  codeH: 0,
})

/** UI KeyConfig → GCOME 高级改键三元组（Type/CodeL/CodeH）。 */
export const keyConfigToGcomeTriple = (config: KeyConfig): GcomeTriple => {
  const keycodeType = parseNumber(config.type) ?? 0
  const gcomeType = KEYCODE_TYPE_TO_GCOME[keycodeType] ?? keycodeType
  const code = parseNumber(config.code) ?? 0

  if (gcomeType === GCOME_MATRIX_TYPE.KEYBOARD) {
    if (HID_MODIFIER_TO_BIT[code]) {
      return { type: gcomeType, codeL: HID_MODIFIER_TO_BIT[code], codeH: 0 }
    }
    return { type: gcomeType, codeL: 0, codeH: code }
  }

  if (gcomeType === GCOME_MATRIX_TYPE.CONSUMER) {
    return { type: gcomeType, codeL: code & 0xff, codeH: (code >> 8) & 0xff }
  }

  if (gcomeType === GCOME_MATRIX_TYPE.SYSTEM) {
    return { type: gcomeType, codeL: code, codeH: 0 }
  }

  return { type: gcomeType, codeL: code & 0xff, codeH: (code >> 8) & 0xff }
}

/** GCOME 三元组 → UI KeyConfig（用于 MT/TGL/DKS 功能框回显）。 */
export const gcomeTripleToKeyConfig = (triple?: GcomeTriple | null): KeyConfig | null => {
  if (!triple?.type && !triple?.codeL && !triple?.codeH) return null

  if (triple.type === GCOME_MATRIX_TYPE.KEYBOARD) {
    const hid = triple.codeH || (triple.codeL ? BIT_TO_HID_MODIFIER[triple.codeL] : undefined)
    return getKeyConfigByTypeCode(0x01, hid) ?? null
  }

  if (triple.type === GCOME_MATRIX_TYPE.CONSUMER) {
    const code = (triple.codeL ?? 0) | ((triple.codeH ?? 0) << 8)
    return getKeyConfigByTypeCode(0x02, code) ?? null
  }

  if (triple.type === GCOME_MATRIX_TYPE.SYSTEM) {
    return getKeyConfigByTypeCode(0x03, triple.codeL) ?? null
  }

  if (triple.type === GCOME_MATRIX_TYPE.MOUSE) {
    const code = triple.codeL | ((triple.codeH ?? 0) << 8)
    return getKeyConfigByTypeCode(0x04, code) ?? null
  }

  if (triple.type === GCOME_MATRIX_TYPE.MACRO) {
    return getKeyConfigByTypeCode(0x08, triple.codeL) ?? null
  }

  return getKeyConfigByTypeCode(triple.type, triple.codeH ?? triple.codeL) ?? null
}

export const buildMtSlotFromKeyConfigs = ({
  click,
  press,
  index = 0,
}: {
  click: KeyConfig
  press: KeyConfig
  index?: number
}): MtSlotInfo => ({
  index,
  tap: keyConfigToGcomeTriple(click),
  hold: keyConfigToGcomeTriple(press),
})

/** 两个物理键互绑：各自 MT slot 的 tap=自身功能、hold=对端功能。 */
export const buildRsMtSlotsFromKeys = ({
  key1,
  key2,
}: {
  key1: KeyConfig
  key2: KeyConfig
}): { mt: MtSlotInfo; peerMt: MtSlotInfo } => ({
  mt: buildMtSlotFromKeyConfigs({ click: key1, press: key2 }),
  peerMt: buildMtSlotFromKeyConfigs({ click: key2, press: key1 }),
})

const isRsSocdKeyType = (keyType?: KeyType) => keyType === 'RS' || keyType === 'SOCD'

/** 解析 RS/SOCD 编辑上下文：当前键上的 setting、编辑态原 pair、对端 setting。 */
export const resolveRsSocdPairContext = ({
  key1,
  key2,
  keySettings,
  editingSetting,
}: {
  key1: KeyConfig
  key2: KeyConfig
  keySettings?: KeySetting[]
  editingSetting?: KeySetting | null
}) => {
  const byKey1 = keySettings ? findSettingByTargetKey(keySettings, key1) : undefined
  const byKey2 = keySettings ? findSettingByTargetKey(keySettings, key2) : undefined

  const editing = isRsSocdKeyType(editingSetting?.keyType) ? editingSetting : undefined
  const editingPeerKeywords = editing?.raw?.relatedKeyKeywords as string | undefined
  const editingPeer =
    editingPeerKeywords && keySettings
      ? keySettings.find((item) => item.keywords === editingPeerKeywords)
      : undefined

  const originalPairSettings = [editing, editingPeer].filter((item): item is KeySetting =>
    Boolean(item),
  )

  const existingSetting =
    (isRsSocdKeyType(byKey1?.keyType) ? byKey1 : undefined) ??
    (editing?.keywords === key1.keywords ? editing : undefined) ??
    byKey1

  const peerExistingSetting =
    (isRsSocdKeyType(byKey2?.keyType) ? byKey2 : undefined) ??
    (editingPeerKeywords === key2.keywords ? editingPeer : undefined) ??
    byKey2

  return { existingSetting, peerExistingSetting, originalPairSettings, editingPeer }
}

/** 编辑 RS/SOCD 时复用原 slot，避免写入新槽位导致列表出现重复绑定。 */
export const applyRsSocdPreferredSlotIndexes = (
  newSetting: KeySetting,
  {
    existingSetting,
    peerExistingSetting,
    editingSetting,
    editingPeer,
  }: {
    existingSetting?: KeySetting
    peerExistingSetting?: KeySetting
    editingSetting?: KeySetting | null
    editingPeer?: KeySetting
  },
) => {
  const mtIndex =
    parseNumber(existingSetting?.raw?.mtIndex) ??
    (editingSetting?.keywords === newSetting.keywords
      ? parseNumber(editingSetting.raw?.mtIndex)
      : undefined) ??
    parseNumber(editingPeer?.raw?.mtIndex)

  const peerMtIndex =
    parseNumber(peerExistingSetting?.raw?.mtIndex) ??
    (editingPeer?.keywords !== newSetting.keywords
      ? parseNumber(editingPeer?.raw?.mtIndex)
      : undefined) ??
    parseNumber(existingSetting?.raw?.peerMtIndex) ??
    (editingSetting?.keywords !== newSetting.keywords
      ? parseNumber(editingSetting?.raw?.mtIndex)
      : undefined)

  if (mtIndex != null) newSetting.raw.mtIndex = mtIndex
  if (peerMtIndex != null) newSetting.raw.peerMtIndex = peerMtIndex
}

/** 收集 RS/SOCD 换键后需要恢复默认的按键（含编辑态原 pair）。 */
export const collectRsSocdRestoreTargets = ({
  keepKeywords,
  keySettings,
  originalPairSettings,
  existingSetting,
  peerExistingSetting,
  keyType,
}: {
  keepKeywords: string[]
  keySettings?: KeySetting[]
  originalPairSettings: KeySetting[]
  existingSetting?: KeySetting
  peerExistingSetting?: KeySetting
  keyType: 'RS' | 'SOCD'
}) => {
  const keep = new Set(keepKeywords)
  const seenKeywords = new Set<string>()
  const targets: KeySetting[] = []

  const candidates = [...originalPairSettings, existingSetting, peerExistingSetting].filter(
    (item): item is KeySetting => Boolean(item),
  )

  for (const setting of candidates) {
    if (setting.keyType !== keyType) continue

    for (const keywords of [setting.keywords, setting.raw?.relatedKeyKeywords as string]) {
      if (!keywords || keep.has(keywords) || seenKeywords.has(keywords)) continue
      seenKeywords.add(keywords)

      const leftover = keySettings?.find((item) => item.keywords === keywords)
      if (leftover?.keyType === keyType) targets.push(leftover)
    }
  }

  return targets
}

export const resolveMtDelayMs = (setting?: KeySetting) => {
  const steps = parseNumber(setting?.raw?.mtDelaySteps)
  if (steps != null) return steps * 10
  const time = parseNumber(setting?.raw?.dualModeKey?.time)
  return time ?? 1000
}

export const isDksSlotEmpty = (slot?: DksSlotInfo | null) => {
  if (!slot) return true
  const noTravel = slot.travels.every((value) => !value)
  const noKeys = slot.keys.every((key) => !key.type && !key.codeL && !key.codeH)
  return noTravel && noKeys
}

export const isMtSlotEmpty = (slot?: MtSlotInfo | null) => {
  if (!slot) return true
  return (
    !slot.tap.type &&
    !slot.tap.codeL &&
    !slot.tap.codeH &&
    !slot.hold.type &&
    !slot.hold.codeL &&
    !slot.hold.codeH
  )
}

export const isTglSlotEmpty = (slot?: TglSlotInfo | null) => {
  if (!slot) return true
  return !slot.type && !slot.codeL && !slot.codeH
}

export const isAdvancedMatrixType = (type?: number) => {
  return type != null && ADVANCED_MATRIX_TYPES.has(type)
}

export const isAdvancedKeyType = (keyType?: KeyType) => {
  return Boolean(keyType && ADVANCED_KEY_TYPES.has(keyType))
}

export const hasAdvancedKeyFeatures = (capabilities?: DeviceManifest['capabilities']) => {
  const cap = capabilities?.find(
    (item): item is KeyFeaturesCapability => item.type === 'keyFeatures',
  )
  return Boolean(cap?.config?.advanced)
}

const createLayoutByIndex = (layoutKeys: LayoutKey[][]) => {
  const layoutByIndex = new Map<number, LayoutKey>()
  layoutKeys.flat().forEach((key) => {
    if (key?.key != null) layoutByIndex.set(key.key, key)
  })
  return layoutByIndex
}

/**
 * GCOME 矩阵条目 → keycode.json / CH585M 语义，供 parseLayerMapToKeySettings 复用。
 */
const normalizeGcomeItemToKeycodeItem = (
  item: keyMapItem,
  originalKey?: LayoutKey,
): keyMapItem | null => {
  const rawType = item.keyAttr.type
  const next: keyMapItem = {
    index: item.index,
    keyAttr: { ...item.keyAttr },
  }

  switch (rawType) {
    case GCOME_MATRIX_TYPE.DISABLE:
      next.keyAttr.type = 0x00
      next.keyAttr.code = [0]
      break
    case GCOME_MATRIX_TYPE.KEYBOARD: {
      next.keyAttr.type = 0x01
      const hid = item.keyAttr.code?.[0]
      const mods = item.keyAttr.modifyKey ?? 0
      if (!hid && mods && BIT_TO_HID_MODIFIER[mods]) {
        next.keyAttr.code = [BIT_TO_HID_MODIFIER[mods]]
      }
      break
    }
    case GCOME_MATRIX_TYPE.MOUSE:
      next.keyAttr.type = 0x04
      break
    case GCOME_MATRIX_TYPE.CONSUMER:
      next.keyAttr.type = 0x02
      break
    case GCOME_MATRIX_TYPE.SYSTEM:
      next.keyAttr.type = 0x03
      break
    case GCOME_MATRIX_TYPE.MACRO:
      next.keyAttr.type = 0x08
      break
    case GCOME_MATRIX_TYPE.FN:
      // 出厂 Fn 不当作已改键；其它 0xF0 按配置键解析。
      if (originalKey?.keywords === 'Fn') return null
      next.keyAttr.type = 0x09
      break
    default:
      break
  }

  return next
}

/**
 * CH585M / keycode.json 写入结构 → GCOME 矩阵 3 字节语义。
 */
const mapKeycodePayloadToGcome = (payload: keyMapItem): keyMapItem => {
  const type = payload.keyAttr.type
  const next: keyMapItem = {
    index: payload.index,
    keyAttr: { ...payload.keyAttr },
  }
  const gcomeType = KEYCODE_TYPE_TO_GCOME[type]
  if (gcomeType == null) return next

  next.keyAttr.type = gcomeType

  if (type === 0x01) {
    const hid = payload.keyAttr.code?.[0]
    if (hid != null && HID_MODIFIER_TO_BIT[hid]) {
      next.keyAttr.modifyKey = HID_MODIFIER_TO_BIT[hid]
      next.keyAttr.code = []
    }
  }

  return next
}

const resolveGcomeTripleLabel = (type?: number, codeL?: number, codeH?: number) => {
  if (type == null || (!type && !codeL && !codeH)) return ''

  if (type === GCOME_MATRIX_TYPE.KEYBOARD) {
    const hid = codeH || (codeL ? BIT_TO_HID_MODIFIER[codeL] : undefined)
    const config = getKeyConfigByTypeCode(0x01, hid)
    return getLocalizedField(config?.alias || config?.name)
  }

  if (type === GCOME_MATRIX_TYPE.CONSUMER) {
    const code = (codeL ?? 0) | ((codeH ?? 0) << 8)
    const config = getKeyConfigByTypeCode(0x02, code)
    return getLocalizedField(config?.alias || config?.name)
  }

  if (type === GCOME_MATRIX_TYPE.SYSTEM) {
    const config = getKeyConfigByTypeCode(0x03, codeL)
    return getLocalizedField(config?.alias || config?.name)
  }

  if (type === GCOME_MATRIX_TYPE.MACRO) {
    return `Macro ${codeL ?? ''}`
  }

  const config = getKeyConfigByTypeCode(type, codeH ?? codeL)
  return getLocalizedField(config?.alias || config?.name)
}

const createAdvancedBaseRaw = (item: keyMapItem, originalKey: LayoutKey) => {
  const mappedConfig = originalKey.keywords
    ? KEYCODE_KEYWORDS_MAP.get(originalKey.keywords)
    : undefined
  return {
    index: item.index,
    type: item.keyAttr.type,
    originalLabel:
      mappedConfig && 'alias' in mappedConfig ? getLocalizedField(mappedConfig.alias) : '',
  }
}

const buildAdvancedSetting = (
  item: keyMapItem,
  originalKey: LayoutKey,
  lists: AdvancedLists,
  layoutByIndex: Map<number, LayoutKey>,
): KeySetting | undefined => {
  if (!originalKey.keywords) return undefined

  const type = item.keyAttr.type
  const slotIndex =
    item.keyAttr.dksIndex ?? item.keyAttr.mtIndex ?? item.keyAttr.tglIndex ?? item.keyAttr.code?.[0]

  const base = {
    keywords: originalKey.keywords,
    isModified: true as const,
    target: null,
  }

  if (type === GCOME_MATRIX_TYPE.DKS) {
    const dks = slotIndex != null ? lists.dksList[slotIndex] : undefined
    const dksKeyLabels = (dks?.keys ?? [])
      .map((key) => resolveGcomeTripleLabel(key.type, key.codeL, key.codeH))
      .filter(Boolean)
      .join('/')

    return {
      ...base,
      keyType: 'DKS',
      raw: {
        ...createAdvancedBaseRaw(item, originalKey),
        dksIndex: slotIndex,
        dks,
        dksKeyLabels,
      },
    }
  }

  if (type === GCOME_MATRIX_TYPE.TGL) {
    const tgl = slotIndex != null ? lists.tglList[slotIndex] : undefined
    return {
      ...base,
      keyType: 'TGL',
      raw: {
        ...createAdvancedBaseRaw(item, originalKey),
        tglIndex: slotIndex,
        tgl,
        clickKeyLabel: resolveGcomeTripleLabel(tgl?.type, tgl?.codeL, tgl?.codeH),
      },
    }
  }

  if (type === GCOME_MATRIX_TYPE.MT) {
    const mt = slotIndex != null ? lists.mtList[slotIndex] : undefined
    return {
      ...base,
      keyType: 'MT',
      raw: {
        ...createAdvancedBaseRaw(item, originalKey),
        mtIndex: slotIndex,
        mt,
        mtDelaySteps: item.keyAttr.code?.[1],
        clickKeyLabel: resolveGcomeTripleLabel(mt?.tap.type, mt?.tap.codeL, mt?.tap.codeH),
        pressKeyLabel: resolveGcomeTripleLabel(mt?.hold.type, mt?.hold.codeL, mt?.hold.codeH),
      },
    }
  }

  if (type === GCOME_MATRIX_TYPE.RS || type === GCOME_MATRIX_TYPE.SOCD) {
    const keyType = type === GCOME_MATRIX_TYPE.RS ? 'RS' : 'SOCD'
    const relatedKeyIndex = item.keyAttr.code?.[1]
    const relatedKey = relatedKeyIndex != null ? layoutByIndex.get(relatedKeyIndex) : undefined
    const mt = slotIndex != null ? lists.mtList[slotIndex] : undefined
    const travel = lists.travelList?.find((entry) => entry.index === item.index)
    const gcomeTravel = travel?.family === KeyTravelFamily.Gcome ? travel.keyTravel : undefined
    const strategy = gcomeTravel?.socdStrategy ?? 0
    let socdSubMode = 1
    let socdSelfPriority = 0
    if (strategy === 0x30) {
      socdSubMode = 3
    } else if (strategy === 0x10) {
      socdSubMode = 2
      socdSelfPriority = 1
    } else if (strategy === 0x20) {
      socdSubMode = 2
      socdSelfPriority = 0
    }

    const relatedPayload = {
      relatedKeyIndex,
      relatedKeyKeywords: relatedKey?.keywords,
      triggerKeyType: 0,
      triggerKeyCode: 0,
      triggerKeyExtCode: 0,
      triggerThreshold: gcomeTravel?.travel ?? 0,
      combineRT: gcomeTravel && gcomeTravel.triggerType !== 0 ? 1 : 0,
    }
    const relatedConfig = relatedKey?.keywords
      ? KEYCODE_KEYWORDS_MAP.get(relatedKey.keywords)
      : undefined

    return {
      ...base,
      keyType,
      raw: {
        ...createAdvancedBaseRaw(item, originalKey),
        mtIndex: slotIndex,
        mt,
        relatedKeyIndex,
        relatedKeyKeywords: relatedKey?.keywords,
        relatedKeyLabel: getLocalizedField(relatedConfig?.alias || relatedConfig?.name),
        rs: keyType === 'RS' ? relatedPayload : undefined,
        socd:
          keyType === 'SOCD'
            ? {
                ...relatedPayload,
                subMode: socdSubMode,
                triggerWhenBottom: 0,
                selfPriority: socdSelfPriority,
              }
            : undefined,
      },
    }
  }

  return undefined
}

/**
 * 读取层映射：标准键归一化后复用 parseLayerMapToKeySettings，
 * 高级键用列表 join 成 KeySetting 给虚拟键盘。
 */
export const parseAdvancedLayerMapToKeySettings = ({
  keyMap,
  layoutKeys,
  advancedLists,
  model,
  factoryKeys,
  displayOverrides,
  isFnLayer = false,
}: {
  keyMap: keyMapItem[]
  layoutKeys: LayoutKey[][]
  advancedLists: AdvancedLists
  model?: string
  /** 当前 Win/Mac 层的 Fn 出厂映射 */
  factoryKeys?: Record<string, string>
  displayOverrides?: KeyDisplayOverride[]
  isFnLayer?: boolean
}): KeySetting[] => {
  const layoutByIndex = createLayoutByIndex(layoutKeys)
  const standardItems: keyMapItem[] = []
  const advancedItems: keyMapItem[] = []

  keyMap.forEach((item) => {
    if (isAdvancedMatrixType(item.keyAttr.type)) {
      advancedItems.push(item)
      return
    }

    const originalKey = layoutByIndex.get(item.index)
    const normalized = normalizeGcomeItemToKeycodeItem(item, originalKey)
    if (normalized) standardItems.push(normalized)
  })

  const settingsByKeywords = new Map<string, KeySetting>()

  parseLayerMapToKeySettings({
    keyMap: standardItems,
    layoutKeys,
    model,
    factoryKeys,
    displayOverrides,
    isFnLayer,
  }).forEach((setting) => {
    settingsByKeywords.set(setting.keywords, setting)
  })

  advancedItems.forEach((item) => {
    const originalKey = layoutByIndex.get(item.index)
    if (!originalKey?.keywords) return
    const setting = buildAdvancedSetting(item, originalKey, advancedLists, layoutByIndex)
    if (setting) settingsByKeywords.set(originalKey.keywords, setting)
  })

  return Array.from(settingsByKeywords.values())
}

const keyTypeToSlotKind = (keyType?: KeyType): AdvancedSlotKind | undefined => {
  if (keyType === 'DKS') return 'dks'
  if (keyType === 'TGL') return 'tgl'
  if (keyType === 'MT' || keyType === 'RS' || keyType === 'SOCD') return 'mt'
  return undefined
}

const readPreferredSlotIndex = (setting?: KeySetting, kind?: AdvancedSlotKind) => {
  if (!setting || !kind) return undefined
  if (kind === 'dks') return parseNumber(setting.raw?.dksIndex)
  if (kind === 'tgl') return parseNumber(setting.raw?.tglIndex)
  return parseNumber(setting.raw?.mtIndex)
}

export const resolveAdvancedSlotIndex = ({
  kind,
  preferredIndex,
  lists,
}: {
  kind: AdvancedSlotKind
  preferredIndex?: number
  lists: AdvancedLists
}): number | undefined => {
  if (preferredIndex != null && preferredIndex >= 0) return preferredIndex

  if (kind === 'dks') return lists.dksList.find((slot) => isDksSlotEmpty(slot))?.index
  if (kind === 'tgl') return lists.tglList.find((slot) => isTglSlotEmpty(slot))?.index
  return lists.mtList.find((slot) => isMtSlotEmpty(slot))?.index
}

export const collectAdvancedListClears = (setting?: KeySetting): AdvancedListClear[] => {
  const kind = keyTypeToSlotKind(setting?.keyType)
  const index = readPreferredSlotIndex(setting, kind)
  if (!kind || index == null) return []

  const clears: AdvancedListClear[] = [{ kind, index }]
  const peerMtIndex = parseNumber(setting?.raw?.peerMtIndex)
  if ((setting?.keyType === 'RS' || setting?.keyType === 'SOCD') && peerMtIndex != null) {
    clears.push({ kind: 'mt', index: peerMtIndex })
  }
  return clears
}

/**
 * 标准改键（常规 / 多媒体 / 宏）→ GCOME 矩阵 payload。
 */
export const buildAdvancedMatrixPayloadFromSetting = (
  setting: KeySetting,
): keyMapItem | undefined => {
  if (isAdvancedKeyType(setting.keyType)) return undefined
  const payload = buildSdkKeyMapPayloadFromSetting(setting)
  if (!payload) return undefined
  return mapKeycodePayloadToGcome(payload)
}

/**
 * 恢复默认键 → GCOME 矩阵 payload（不含清 slot，清 slot 见 collectAdvancedListClears）。
 */
export const buildAdvancedRestoreMatrixPayload = ({
  setting,
  defaultKey,
  factoryKeywords,
}: {
  setting?: KeySetting
  defaultKey: {
    keywords?: string
    type?: unknown
    code?: unknown
    confIndex?: unknown
    key?: unknown
  }
  factoryKeywords?: string
}): keyMapItem | undefined => {
  const payload = buildRestoreKeyMapPayload({ setting, defaultKey, factoryKeywords })
  if (!payload) return undefined
  return mapKeycodePayloadToGcome(payload)
}

const resolveMtDelaySteps = (setting: KeySetting) => {
  const fromRaw = parseNumber(setting.raw?.mtDelaySteps)
  if (fromRaw != null) return fromRaw
  const time = parseNumber(setting.raw?.dualModeKey?.time)
  if (time == null) return 0
  return Math.max(0, Math.min(255, Math.round(time / 10)))
}

/**
 * 高级改键写入计划：分配/复用 slot，生成列表写入 + 矩阵绑定。
 * 槽位满时返回 undefined。
 */
export const buildAdvancedWritePlan = ({
  setting,
  lists,
  existingSetting,
  peerExistingSetting,
}: {
  setting: KeySetting
  lists: AdvancedLists
  existingSetting?: KeySetting
  peerExistingSetting?: KeySetting
}): AdvancedWritePlan | undefined => {
  const kind = keyTypeToSlotKind(setting.keyType)
  if (!kind) return undefined

  const index = parseNumber(setting.raw?.index)
  if (index == null) return undefined

  const preferred =
    readPreferredSlotIndex(setting, kind) ?? readPreferredSlotIndex(existingSetting, kind)
  const slotIndex = resolveAdvancedSlotIndex({ kind, preferredIndex: preferred, lists })
  if (slotIndex == null) return undefined

  const existingKind = keyTypeToSlotKind(existingSetting?.keyType)
  const peerExistingKind = keyTypeToSlotKind(peerExistingSetting?.keyType)
  const listClears = [
    ...(existingKind && existingKind !== kind ? collectAdvancedListClears(existingSetting) : []),
    ...(peerExistingKind && peerExistingKind !== kind
      ? collectAdvancedListClears(peerExistingSetting)
      : []),
  ]

  if (setting.keyType === 'DKS') {
    const slot = setting.raw?.dks as DksSlotInfo | undefined
    if (!slot) return undefined
    const nextSlot = { ...slot, index: slotIndex }
    return {
      listWrites: [{ kind: 'dks', index: slotIndex, slot: nextSlot }],
      matrixItems: [{ index, keyAttr: { type: GCOME_MATRIX_TYPE.DKS, dksIndex: slotIndex } }],
      listClears,
    }
  }

  if (setting.keyType === 'TGL') {
    const slot = setting.raw?.tgl as TglSlotInfo | undefined
    if (!slot) return undefined
    const nextSlot = { ...slot, index: slotIndex }
    return {
      listWrites: [{ kind: 'tgl', index: slotIndex, slot: nextSlot }],
      matrixItems: [{ index, keyAttr: { type: GCOME_MATRIX_TYPE.TGL, tglIndex: slotIndex } }],
      listClears,
    }
  }

  if (setting.keyType === 'MT') {
    const slot = setting.raw?.mt as MtSlotInfo | undefined
    if (!slot) return undefined
    const nextSlot = { ...slot, index: slotIndex }
    const delaySteps = resolveMtDelaySteps(setting)
    return {
      listWrites: [{ kind: 'mt', index: slotIndex, slot: nextSlot }],
      matrixItems: [
        {
          index,
          keyAttr: {
            type: GCOME_MATRIX_TYPE.MT,
            mtIndex: slotIndex,
            code: [slotIndex, delaySteps],
          },
        },
      ],
      listClears,
    }
  }

  if (setting.keyType === 'RS' || setting.keyType === 'SOCD') {
    const slot = setting.raw?.mt as MtSlotInfo | undefined
    if (!slot) return undefined
    const relatedKeyIndex = parseNumber(
      setting.raw?.relatedKeyIndex ?? setting.raw?.rs?.relatedKeyIndex,
    )
    const matrixType = setting.keyType === 'RS' ? GCOME_MATRIX_TYPE.RS : GCOME_MATRIX_TYPE.SOCD
    const nextSlot = { ...slot, index: slotIndex }
    const writes: AdvancedListWrite[] = [{ kind: 'mt', index: slotIndex, slot: nextSlot }]
    const matrixItems: keyMapItem[] = [
      {
        index,
        keyAttr: {
          type: matrixType,
          code: [slotIndex, relatedKeyIndex ?? 0],
        },
      },
    ]

    const peerSlot = setting.raw?.peerMt as MtSlotInfo | undefined
    const peerIndex = parseNumber(setting.raw?.peerKeyIndex)
    const peerMtIndex = resolveAdvancedSlotIndex({
      kind: 'mt',
      preferredIndex:
        parseNumber(setting.raw?.peerMtIndex) ?? readPreferredSlotIndex(peerExistingSetting, 'mt'),
      lists: {
        ...lists,
        mtList: lists.mtList.map((item) => (item.index === slotIndex ? nextSlot : item)),
      },
    })

    if (peerSlot && peerIndex != null && peerMtIndex != null) {
      writes.push({ kind: 'mt', index: peerMtIndex, slot: { ...peerSlot, index: peerMtIndex } })
      matrixItems.push({
        index: peerIndex,
        keyAttr: {
          type: matrixType,
          code: [peerMtIndex, index],
        },
      })
    }

    return { listWrites: writes, matrixItems, listClears }
  }

  return undefined
}

/**
 * 从高级改键切回标准改键，或恢复默认：矩阵走标准 payload，并清旧 slot。
 */
export const buildAdvancedStandardWritePlan = ({
  matrixItem,
  existingSetting,
}: {
  matrixItem: keyMapItem
  existingSetting?: KeySetting
}): AdvancedWritePlan => {
  return {
    listWrites: [],
    matrixItems: [matrixItem],
    listClears: collectAdvancedListClears(existingSetting),
  }
}
